import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type { Location } from '@envoy/components-communication';
import Component from '@glimmer/component';
import { all, didCancel, enqueueTask, restartableTask } from 'ember-concurrency';
import type CommunicationsService from 'garaje/services/communications-service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ReactRendererService from 'garaje/services/react-renderer';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import {
  COMMUNICATIONS_FAN_OUT_CONCURRENCY,
  decorateLocationForCommunications,
  fetchCommunicationsEligibility,
  MPB_FEATURE_FLAG,
  type ScopedSubLookup,
} from 'garaje/utils/mpb-location-eligibility';
import type { Root } from 'react-dom/client';

interface MessageLogNewArgs {
  locationId?: string;
  locationName?: string;
  redirectRoute: string;
}

export default class CommunicationsMessageLogNewComponent extends Component<MessageLogNewArgs> {
  @service declare reactRenderer: ReactRendererService;
  @service declare router: RouterService;
  @service declare flashMessages: FlashMessagesService;
  @service declare communicationsService: CommunicationsService;
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare store: Store;
  @service declare state: StateService;

  private root?: Root;

  // Performed via the parent task so supersede/teardown cancels its queued performs.
  private fetchEligibilityTask = enqueueTask(
    { maxConcurrency: COMMUNICATIONS_FAN_OUT_CONCURRENCY },
    async (companyId: string, locationId: string): Promise<readonly [string, ScopedSubLookup]> => [
      locationId,
      await fetchCommunicationsEligibility(this.store, companyId, locationId),
    ],
  );

  // Returns the map (not a shared tracked field) so a superseded load can't leak
  // a half-built map to `getLocations`.
  private loadCommunicationsEligibilityTask = restartableTask(
    async (locationIds: readonly string[]): Promise<ReadonlyMap<string, ScopedSubLookup>> => {
      const companyId = this.state.currentCompany?.id;
      if (!companyId || this.isDestroying || this.isDestroyed) return new Map();
      // Must be ember-concurrency's `all`, not native Promise.all: it links the
      // child performs to this task so supersede/teardown cancels them.
      const entries = await all(locationIds.map((id) => this.fetchEligibilityTask.perform(companyId, id)));
      // Company switched mid-load → fail open rather than decorate the new company.
      if (this.state.currentCompany?.id !== companyId) return new Map();
      return new Map(entries);
    },
  );

  private async getLocations(): Promise<Location[]> {
    await this.skinnyLocations.loadAllTask.perform();
    // Match the non-MPB GraphQL fallback (which excludes deactivated locations);
    // without this, MPB-on surfaces disabled rows the MPB-off picker hides.
    const skinny = this.skinnyLocations.currentCompanyLocations
      .filter((l) => !l.disabled && !l.disabledToEmployeesAt)
      .map((l) => ({ id: l.id, name: l.name }));
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    let subsMap: ReadonlyMap<string, ScopedSubLookup> = new Map();
    if (isMpbEnabled) {
      try {
        subsMap = (await this.loadCommunicationsEligibilityTask.perform(skinny.map((l) => l.id))) ?? new Map();
      } catch (e) {
        // Supersede and teardown both reject with TaskCancellation; swallow it
        // and fall through with an empty map (decorate fails open; backend gates).
        if (!didCancel(e)) throw e;
      }
    }
    return skinny.map((l) => decorateLocationForCommunications(l, isMpbEnabled, subsMap));
  }

  @action
  async injectComponent(): Promise<void> {
    const canSendEmergencyNotifications = await this.communicationsService.canSendEmergencyNotifications();
    const canSendTakeovers = this.communicationsService.canSendTakeovers;
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    const { SendMessage } = await import('reactRichComponents/communication/SendMessage');

    // prevent error from being thrown if element is already destroyed from navigating away
    if (!document.getElementById('react__communications__send-message')) {
      return;
    }

    this.root = await this.reactRenderer.render(SendMessage, 'react__communications__send-message', {
      service: {
        canSelectScimGroups: this.communicationsService.canSelectScimGroups,
        showEmployeeSelect: this.communicationsService.showEmployeeSelect,
        showVisitorSelect: this.communicationsService.showVisitorSelect,
        canSendEmergencyNotifications,
        canSendTakeovers,
        getAudiences: (locationId: string) => this.communicationsService.getAudiences(locationId),
        // MPB-only; non-MPB tenants fall back to React's internal GraphQL fetch (unchanged).
        getLocations: isMpbEnabled ? () => this.getLocations() : undefined,
      },
      locationId: this.args.locationId,
      locationName: this.args.locationName,
      onSent: () => this.onSent(),
      onError: (errorMessage, error) => this.onError(errorMessage, error),
      // Mounted under both per-location (`communications.message-log.new`) and
      // global-overview (`location-overview.communications.message-log.new`) routes.
      // Point at the matching templates tree so a global-overview user clicking
      // "Create template" doesn't get pulled into a `current-location` context (SQ-7332).
      createTemplateUrl: this.router.currentRouteName?.includes('location-overview')
        ? '/location-overview/communications/templates'
        : '/communications/templates',
    });
  }

  @action
  destroyComponent(): void {
    void this.loadCommunicationsEligibilityTask.cancelAll();
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }

  @action
  onSent(): void {
    void this.router.transitionTo(this.args.redirectRoute);
    this.flashMessages.showAndHideFlash('success', 'Sent successfully');
  }

  @action
  onError(errorMessage: string, error: unknown): void {
    this.flashMessages.showAndHideFlash('error', errorMessage);

    this.workplaceMetrics.logMonitorError({
      event: 'COMMUNICATIONS_SEND_MESSAGE_COMPONENT_ERROR',
      debugExtras: { errorMessage },
      error,
    });
  }
}
